/**
 * Documentation Manager - View and download documentation files
 */
import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FileText,
  Download,
  Eye,
  RefreshCw,
  ExternalLink,
  Copy,
  Check,
  File,
  FileCode
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = window.location.origin || '';

export default function DocumentationManager() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/docs/`);
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const viewDocument = async (doc) => {
    try {
      const res = await fetch(`${API_URL}/api/docs/view/${doc.path}`);
      const data = await res.json();
      setSelectedDoc(doc);
      setDocContent(data.content);
    } catch (error) {
      console.error('Error loading document:', error);
      toast.error('Failed to load document');
    }
  };

  const downloadDocument = (doc) => {
    window.open(`${API_URL}/api/docs/download/${doc.path}`, '_blank');
  };

  const copyToClipboard = async () => {
    if (docContent) {
      await navigator.clipboard.writeText(docContent);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (extension) => {
    if (extension === '.md') return <FileText className="w-5 h-5 text-blue-500" />;
    if (['.json', '.js', '.py'].includes(extension)) return <FileCode className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="documentation-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Documentation</h1>
            <p className="text-slate-500">Technical documentation and integration guides</p>
          </div>
          <Button variant="outline" onClick={loadDocuments} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Documents List */}
        <Card>
          <CardHeader>
            <CardTitle>Available Documents</CardTitle>
            <CardDescription>Click to view or download documentation files</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No documentation files found</p>
                <p className="text-sm">Add files to /app/docs/ to see them here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Modified</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.path} className="cursor-pointer hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {getFileIcon(doc.extension)}
                          <div>
                            <p className="font-medium">{doc.name}</p>
                            <p className="text-sm text-slate-500">{doc.path}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {formatFileSize(doc.size)}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {formatDate(doc.modified)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => viewDocument(doc)}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => downloadDocument(doc)}>
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(`${API_URL}/api/docs/raw/${doc.path}`, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Links</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <a 
                href={`${API_URL}/api/docs/raw/ZOHO_CRM_STUDENT_ACTIVITIES_MODULE.md`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg border hover:bg-slate-50 transition-colors"
              >
                <FileText className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-medium">Zoho CRM Integration Guide</p>
                  <p className="text-sm text-slate-500">Custom module fields and setup instructions</p>
                </div>
                <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Document Viewer Dialog */}
        <Dialog open={!!selectedDoc} onOpenChange={() => { setSelectedDoc(null); setDocContent(null); }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  {selectedDoc && getFileIcon(selectedDoc.extension)}
                  {selectedDoc?.name}
                </DialogTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={copyToClipboard}>
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                  {selectedDoc && (
                    <Button size="sm" variant="outline" onClick={() => downloadDocument(selectedDoc)}>
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  )}
                </div>
              </div>
            </DialogHeader>
            
            <div className="flex-1 overflow-auto mt-4 border rounded-lg">
              {selectedDoc?.extension === '.md' ? (
                <div className="prose prose-slate max-w-none p-6">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {docContent || ''}
                  </ReactMarkdown>
                </div>
              ) : (
                <pre className="p-4 text-sm bg-slate-50 overflow-auto whitespace-pre-wrap">
                  {docContent}
                </pre>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
